<template>
  <div class="discount-transfer">
    <div class="row">
      <div class="col-lg-12">
        <label class="control-label">Zakres gałęzi</label>
        <div class="radio">
          <label>
            <input v-model="scope" type="radio" value="all" />
            Całe drzewo
          </label>
        </div>
        <div class="radio">
          <label :class="{ 'text-muted': !scopeRoots.length }">
            <input
              v-model="scope"
              type="radio"
              value="checked"
              :disabled="!scopeRoots.length"
            />
            Zaznaczone gałęzie ({{ scopeRoots.length }})
          </label>
          <div v-if="scopeRoots.length" class="scope-roots">
            <span v-for="root in scopeRoots" :key="root.pk" class="label">
              {{ root.title }}
            </span>
          </div>
          <span v-else class="help-block">
            Zaznacz w drzewie rabaty, od których mają zaczynać się gałęzie.
          </span>
        </div>
      </div>
    </div>

    <hr />

    <div class="row">
      <div class="col-lg-4">
        <h4>Eksport</h4>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="exporting"
          @click="exportTree"
        >
          <font-awesome-icon v-if="exporting" icon="spinner" spin />
          <span v-else>Eksportuj do JSON</span>
        </button>
      </div>

      <div v-if="canImport" class="col-lg-8">
        <h4>Import</h4>
        <div class="flex import-row">
          <input
            ref="fileInput"
            type="file"
            accept=".json,application/json"
            @change="changeFile"
          />
          <button
            type="button"
            class="btn btn-default"
            :disabled="!file || checking || importing"
            @click="checkFile"
          >
            <font-awesome-icon v-if="checking" icon="spinner" spin />
            <span v-else>Sprawdź plik</span>
          </button>
        </div>
        <span class="help-block">
          Importowane są tylko wartości zmienione w pliku. Wartości zmienione w
          systemie po eksporcie nie zostaną nadpisane.
        </span>
      </div>
    </div>

    <div v-if="result" class="import-result">
      <hr />
      <div v-if="result.legacy" class="alert alert-warning">
        Plik w starym formacie (bez wartości z chwili eksportu) - zostaną
        zaimportowane wszystkie wartości różne od obecnych, także te zmienione w
        systemie po eksporcie.
      </div>
      <div v-if="result.detail && !result.changes" class="alert alert-danger">
        {{ detailText }}
      </div>

      <template v-if="result.changes">
        <p>
          <span v-if="result.file?.exported_at">
            Eksport z {{ formatDate(result.file.exported_at) }}.
          </span>
          <span v-if="result.file?.root_pks?.length">
            Plik obejmuje gałęzie rabatów o ID:
            {{ result.file.root_pks.join(", ") }}.
          </span>
        </p>
        <dl class="dl-horizontal">
          <dt>Zmiany:</dt>
          <dd>{{ result.changes.length }}</dd>
          <dt>Wyzerowane kaskadą:</dt>
          <dd>{{ result.zeroed.length }}</dd>
          <dt>Nakłady w innych kategoriach:</dt>
          <dd>{{ result.synced.length }}</dd>
          <dt>Pominięte:</dt>
          <dd>{{ result.skipped.length }}</dd>
          <dt>Błędy:</dt>
          <dd :class="{ 'text-danger': result.errors.length }">
            {{ result.errors.length }}
          </dd>
        </dl>

        <div v-if="result.errors.length" class="alert alert-danger">
          Plik zawiera błędy - import nie jest możliwy, dopóki nie zostaną
          poprawione.
        </div>

        <div
          v-for="section in sections"
          :key="section.key"
          class="result-section"
        >
          <template v-if="result[section.key].length">
            <h5 :class="section.titleClass">{{ section.title }}</h5>
            <p v-if="section.help" class="text-muted">{{ section.help }}</p>
            <div class="table-responsive result-table">
              <table class="table table-striped table-condensed">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Rabat</th>
                    <th v-for="column in section.columns" :key="column.key">
                      {{ column.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in result[section.key]"
                    :key="section.key + index"
                  >
                    <td>{{ item.id ?? "-" }}</td>
                    <td>
                      {{ item.title }}
                      <small v-if="item.parent_title" class="text-muted">
                        ({{ item.parent_title }})
                      </small>
                      <span v-if="item.hidden" class="label">ukryty</span>
                    </td>
                    <td v-for="column in section.columns" :key="column.key">
                      {{
                        column.format ? column.format(item) : item[column.key]
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>

        <div class="text-right">
          <button type="button" class="btn btn-white" @click="clearResult">
            Anuluj
          </button>
          <button
            v-if="!result.committed"
            type="button"
            class="btn btn-primary"
            :disabled="!canCommit || importing"
            @click="commitImport"
          >
            <font-awesome-icon v-if="importing" icon="spinner" spin />
            <span v-else>Importuj zmiany ({{ result.changes.length }})</span>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

const FIELD_LABELS = {
  value: "Wartość",
  secondary_value: "Wartość dodatkowa",
};

const EXPORT_ERROR = "Nie udało się wyeksportować drzewa rabatów.";
const IMPORT_ERROR = "Nie udało się zaimportować pliku.";
const IMPORT_SUCCESS = "Rabaty zostały zaimportowane.";

// The toaster renders HTML, while the messages carry names of products.
const escapeHtml = (text) =>
  String(text).replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[char])
  );

const detailMessages = (data) => {
  const detail = data?.detail;
  if (!detail) {
    return [];
  }
  return Array.isArray(detail) ? detail.map(String) : [String(detail)];
};

export default {
  name: "DiscountTreeTransfer",
  props: {
    // Checked nodes of the tree without the ones inside another checked
    // branch - the roots of the branches to export or import.
    scopeRoots: { type: Array, default: () => [] },
    canImport: { type: Boolean, default: false },
  },
  emits: ["imported"],

  setup(props, { emit }) {
    const store = useStore();
    const scope = ref("all");
    const exporting = ref(false);
    const checking = ref(false);
    const importing = ref(false);
    const file = ref(null);
    const fileInput = ref(null);
    const result = ref(null);

    const rootPks = computed(() =>
      scope.value === "checked" ? props.scopeRoots.map((node) => node.pk) : []
    );

    const canCommit = computed(
      () =>
        result.value?.can_commit === true &&
        !result.value.committed &&
        result.value.changes?.length > 0
    );

    const detailText = computed(() => detailMessages(result.value).join(" "));

    const toast = (type, content) => {
      store.dispatch("showToaster", { type, content });
    };

    const clearResult = () => {
      result.value = null;
    };

    // A checked result is valid only for the file and the scope it was made
    // for - the import must save exactly what was shown.
    watch(rootPks, (newValue, oldValue) => {
      if (newValue.join(",") !== oldValue.join(",")) {
        clearResult();
      }
    });
    watch(
      () => props.scopeRoots.length,
      (length) => {
        if (!length && scope.value === "checked") {
          scope.value = "all";
        }
      }
    );

    const exportTree = () => {
      exporting.value = true;
      store
        .dispatch("exportDiscountTree", { rootPks: rootPks.value })
        .catch(async (error) => {
          let messages = [];
          try {
            messages = detailMessages(
              JSON.parse(await error.response.data.text())
            );
          } catch (e) {
            messages = [];
          }
          toast(
            "error",
            messages.length
              ? messages.map(escapeHtml).join("<br>")
              : EXPORT_ERROR
          );
        })
        .finally(() => {
          exporting.value = false;
        });
    };

    const changeFile = (event) => {
      file.value = event.target.files[0] || null;
      clearResult();
    };

    const runImport = (commit) =>
      store.dispatch("importDiscountTree", {
        file: file.value,
        rootPks: rootPks.value,
        commit,
      });

    const checkFile = () => {
      if (!file.value) {
        return;
      }
      checking.value = true;
      clearResult();
      runImport(false)
        .then((response) => {
          result.value = response.data || { detail: IMPORT_ERROR };
          if (response.status !== 200) {
            toast("error", escapeHtml(detailText.value || IMPORT_ERROR));
          }
        })
        .catch(() => toast("error", IMPORT_ERROR))
        .finally(() => {
          checking.value = false;
        });
    };

    const commitImport = () => {
      if (!canCommit.value) {
        return;
      }
      importing.value = true;
      runImport(true)
        .then((response) => {
          result.value = response.data || { detail: IMPORT_ERROR };
          if (response.status === 200 && response.data?.committed) {
            toast("success", IMPORT_SUCCESS);
            file.value = null;
            if (fileInput.value) {
              fileInput.value.value = "";
            }
            emit("imported");
            return;
          }
          toast("error", escapeHtml(detailText.value || IMPORT_ERROR));
        })
        .catch(() => toast("error", IMPORT_ERROR))
        .finally(() => {
          importing.value = false;
        });
    };

    const formatDate = (value) => {
      const date = new Date(value);
      return isNaN(date) ? value : date.toLocaleString("pl-PL");
    };

    const sections = [
      {
        key: "errors",
        title: "Błędy",
        titleClass: "text-danger",
        columns: [{ key: "message", label: "Opis" }],
      },
      {
        key: "changes",
        title: "Zmiany z pliku",
        columns: [
          {
            key: "field",
            label: "Pole",
            format: (item) => FIELD_LABELS[item.field] || item.field,
          },
          { key: "old", label: "Obecnie" },
          { key: "new", label: "Po imporcie" },
        ],
      },
      {
        key: "zeroed",
        title: "Wyzerowane przez kaskadę",
        help: "Niezerowa wartość rabatu zeruje wartości jego przodków i potomków - tak jak przy edycji w drzewie.",
        columns: [
          { key: "old", label: "Obecnie" },
          { key: "cause", label: "Przez rabat ID" },
        ],
      },
      {
        key: "synced",
        title: "Nakłady w innych kategoriach produktu",
        help: "Rabat nakładu przenosi swoją wartość na ten sam nakład produktu w pozostałych kategoriach.",
        columns: [
          { key: "old", label: "Obecnie" },
          { key: "new", label: "Po imporcie" },
          { key: "cause", label: "Przez rabat ID" },
        ],
      },
      {
        key: "skipped",
        title: "Pominięte",
        columns: [
          {
            key: "field",
            label: "Pole",
            format: (item) => FIELD_LABELS[item.field] || "",
          },
          { key: "message", label: "Powód" },
        ],
      },
    ];

    return {
      scope,
      exporting,
      checking,
      importing,
      file,
      fileInput,
      result,
      canCommit,
      detailText,
      sections,
      exportTree,
      changeFile,
      checkFile,
      commitImport,
      clearResult,
      formatDate,
    };
  },
};
</script>

<style scoped>
.scope-roots {
  margin: 4px 0 0 20px;
}

.scope-roots .label {
  display: inline-block;
  margin: 0 4px 4px 0;
}

.import-row {
  align-items: center;
  gap: 10px;
}

.result-table {
  max-height: 320px;
  overflow-y: auto;
}

.result-section h5 {
  margin-top: 15px;
}
</style>
